import { SelectedGame } from "../types/game";

/**
 * Calculate the number of games that have been picked
 */
export const calculatePickedCount = (
  games: SelectedGame[],
  picks: Record<number, number>
): number => {
  return games.filter((g) => picks[Number(g.game.id)] !== undefined).length;
};

/**
 * Calculate the number of locks currently active
 */
export const calculateLockCount = (locks: Record<number, boolean>): number => {
  return Object.values(locks).filter((l) => l).length;
};

/**
 * Check if a new lock can be added based on current locks and max allowed
 */
export const canAddLock = (
  currentLocks: Record<number, boolean>,
  maxLocks: number
): boolean => {
  const currentLockCount = Object.values(currentLocks).filter((l) => l).length;
  return currentLockCount < maxLocks;
};

/**
 * Filter games based on whether to show only unpicked games
 */
export const filterGames = (
  games: SelectedGame[],
  picks: Record<number, number>,
  showUnpickedOnly: boolean
): SelectedGame[] => {
  if (!showUnpickedOnly) return games;
  return games.filter((g) => picks[Number(g.game.id)] === undefined);
};

/**
 * Get the game of the week information for display
 */
export const getGameOfTheWeekInfo = (
  games: SelectedGame[],
  gameOfTheWeek: number | undefined
): { homeTeam: string; awayTeam: string } | null => {
  if (!gameOfTheWeek) return null;

  const game = games.find((g) => g.game.id === gameOfTheWeek);
  if (!game) return null;

  return {
    homeTeam: game.homeTeam.abbreviation,
    awayTeam: game.awayTeam.abbreviation,
  };
};

/**
 * Validate if a pick can be made (game hasn't started, etc.)
 */
export const canMakePick = (
  game: SelectedGame,
  isGameStarted: (time: string) => boolean
): boolean => {
  return !isGameStarted(game.game.time);
};

/**
 * Validate if a lock can be toggled (game hasn't started, etc.)
 */
export const canToggleLock = (
  game: SelectedGame,
  isGameStarted: (time: string) => boolean
): boolean => {
  return !isGameStarted(game.game.time);
};

/**
 * Create a pick object for API submission
 */
export const createPickObject = (
  gameId: number,
  teamId: number,
  isLocked: boolean = false
): { gameId: number; pick: number; lock: boolean } => {
  return {
    gameId,
    pick: teamId,
    lock: isLocked,
  };
};

/**
 * Calculate pagination info
 */
export const calculatePaginationInfo = (
  totalItems: number,
  rowsPerPage: number,
  currentPage: number
): {
  totalPages: number;
  startIndex: number;
  endIndex: number;
  needsPagination: boolean;
} => {
  const totalPages = Math.ceil(totalItems / rowsPerPage);
  const startIndex = currentPage * rowsPerPage;
  const endIndex = Math.min(startIndex + rowsPerPage, totalItems);
  const needsPagination = totalItems > rowsPerPage;

  return {
    totalPages,
    startIndex,
    endIndex,
    needsPagination,
  };
};
